import { useEffect, useRef } from 'react';

export const useDialogKeyboardNavigation = (
  isOpen: boolean,
  onEscape: () => void
) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const escapeHandler = useRef(onEscape);
  escapeHandler.current = onEscape;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!isOpen || !dialog) {
      return;
    }

    const previousFocus = document.activeElement as HTMLElement | null;
    const getButtons = () => Array.from(
      dialog.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')
    );
    const buttons = getButtons();
    const initialButton = dialog.querySelector<HTMLButtonElement>('[data-dialog-primary]') || buttons[0];
    initialButton?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentButtons = getButtons();
      const focusedIndex = currentButtons.indexOf(document.activeElement as HTMLButtonElement);
      const currentIndex = focusedIndex >= 0 ? focusedIndex : 0;

      if (event.key === 'Tab') {
        event.preventDefault();
        const step = event.shiftKey ? -1 : 1;
        currentButtons[(currentIndex + step + currentButtons.length) % currentButtons.length]?.focus();
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        currentButtons[(currentIndex + 1) % currentButtons.length]?.focus();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        currentButtons[(currentIndex - 1 + currentButtons.length) % currentButtons.length]?.focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        currentButtons[currentIndex]?.click();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        escapeHandler.current();
      }
    };

    dialog.addEventListener('keydown', handleKeyDown);
    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  return dialogRef;
};
